import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Camera, MessagesSquare, Check, X, Eye, EyeOff, Flag, Loader2 } from "lucide-react";
import { BACKEND_BASE, modPhotos, modPhotoAction, modChats, modChatAction } from "@/lib/api";

const Pill = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${active ? "bg-navy text-white" : "border border-border bg-white text-navy hover:bg-parchment"}`}>
    {children}
  </button>
);

export function CommunityModeration({ onBack }) {
  const [tab, setTab] = useState("photos");
  const [photoStatus, setPhotoStatus] = useState("pending");
  const [chatStatus, setChatStatus] = useState("flagged");
  const [photos, setPhotos] = useState([]);
  const [pCounts, setPCounts] = useState({});
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPhotos = useCallback((s) => {
    setLoading(true);
    modPhotos(s).then((d) => { setPhotos(d.photos || []); setPCounts(d.counts || {}); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed")).finally(() => setLoading(false));
  }, []);
  const loadChats = useCallback((s) => {
    setLoading(true);
    modChats(s).then((d) => setThreads(d.threads || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed")).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (tab === "photos") loadPhotos(photoStatus); }, [tab, photoStatus, loadPhotos]);
  useEffect(() => { if (tab === "chats") loadChats(chatStatus); }, [tab, chatStatus, loadChats]);

  const actPhoto = async (id, action) => {
    try { await modPhotoAction(id, action); toast.success(action === "approve" ? "Photo approved" : "Photo rejected"); loadPhotos(photoStatus); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const actChat = async (id, action) => {
    try { await modChatAction(id, action); toast.success(action === "publish" ? "Published" : "Hidden"); loadChats(chatStatus); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="community-moderation">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="mb-1 font-display text-3xl font-black text-navy">Community Moderation</h1>
      <p className="mb-5 text-sm text-muted-foreground">Approve contributed photos and review flagged or held discussions.</p>

      <div className="mb-5 flex gap-2">
        <Pill active={tab === "photos"} onClick={() => setTab("photos")} testid="mod-tab-photos"><Camera className="h-4 w-4" /> Photos</Pill>
        <Pill active={tab === "chats"} onClick={() => setTab("chats")} testid="mod-tab-chats"><MessagesSquare className="h-4 w-4" /> Discussions</Pill>
      </div>

      {tab === "photos" && (
        <>
          <div className="mb-4 flex gap-2">
            {["pending", "approved", "rejected"].map((s) => (
              <Pill key={s} active={photoStatus === s} onClick={() => setPhotoStatus(s)} testid={`mod-photo-status-${s}`}>
                {s} {pCounts[s] != null ? <span className="opacity-70">({pCounts[s]})</span> : null}
              </Pill>
            ))}
          </div>
          {loading ? <div className="h-32 animate-pulse rounded-xl bg-parchment" />
            : photos.length === 0 ? <p className="text-sm text-muted-foreground">No {photoStatus} photos.</p>
            : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {photos.map((p) => (
                  <div key={p.id} className="overflow-hidden rounded-xl border border-border bg-white" data-testid={`mod-photo-${p.id}`}>
                    <img src={`${BACKEND_BASE}${p.url}`} alt={p.caption || p.place_name} className="aspect-video w-full object-cover" />
                    <div className="p-3">
                      <p className="truncate text-xs font-semibold text-navy">{p.place_name}</p>
                      {p.caption && <p className="line-clamp-2 text-xs text-ink">{p.caption}</p>}
                      <p className="text-[10px] text-muted-foreground">by {p.credit}</p>
                      {photoStatus !== "approved" && (
                        <div className="mt-2 flex gap-2">
                          <button onClick={() => actPhoto(p.id, "approve")} className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-green-600 py-1.5 text-xs font-bold text-white hover:bg-green-700" data-testid={`mod-photo-approve-${p.id}`}><Check className="h-3.5 w-3.5" /> Approve</button>
                          <button onClick={() => actPhoto(p.id, "reject")} className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-oldglory-red py-1.5 text-xs font-bold text-white hover:bg-oldglory-dark" data-testid={`mod-photo-reject-${p.id}`}><X className="h-3.5 w-3.5" /> Reject</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </>
      )}

      {tab === "chats" && (
        <>
          <div className="mb-4 flex gap-2">
            {["flagged", "pending", "hidden", "published"].map((s) => (
              <Pill key={s} active={chatStatus === s} onClick={() => setChatStatus(s)} testid={`mod-chat-status-${s}`}>{s}</Pill>
            ))}
          </div>
          {loading ? <div className="h-32 animate-pulse rounded-xl bg-parchment" />
            : threads.length === 0 ? <p className="text-sm text-muted-foreground">No {chatStatus} discussions.</p>
            : (
              <div className="space-y-3">
                {threads.map((t) => (
                  <div key={t.id} className="rounded-xl border border-border bg-white p-4" data-testid={`mod-thread-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="rounded bg-navy/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-navy/70">{t.topic}</span>
                          <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${t.status === "published" ? "bg-green-100 text-green-700" : t.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-oldglory-red/10 text-oldglory-red"}`}>{t.status}</span>
                          {t.flags > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-oldglory-red"><Flag className="h-3 w-3" /> {t.flags}</span>}
                        </div>
                        <p className="font-display text-base font-bold text-navy">{t.title}</p>
                        <p className="mt-0.5 line-clamp-2 text-sm text-ink">{t.body}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{t.place_name} · by {t.author_name}</p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-2">
                        <button onClick={() => actChat(t.id, "publish")} className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700" data-testid={`mod-thread-publish-${t.id}`}><Eye className="h-3.5 w-3.5" /> Publish</button>
                        <button onClick={() => actChat(t.id, "hide")} className="inline-flex items-center gap-1 rounded-md bg-oldglory-red px-3 py-1.5 text-xs font-bold text-white hover:bg-oldglory-dark" data-testid={`mod-thread-hide-${t.id}`}><EyeOff className="h-3.5 w-3.5" /> Hide</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </>
      )}
    </div>
  );
}
